import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getWebOrigin } from '../apiBase';

/**
 * Панель согласия на обработку ПДн: политика + чекбокс + кнопка принятия
 * (для аккаунтов, созданных до введения согласия по 152-ФЗ).
 */
export default function ConsentAcceptPanel({ onAccepted, context = 'legacy' }) {
    const { t } = useTranslation(['consent', 'common']);
    const [version, setVersion] = useState(null);
    const [checked, setChecked] = useState(false);
    const [highlight, setHighlight] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const token = localStorage.getItem('token');
    const policySrc = `${getWebOrigin()}/privacy-policy`;

    const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        axios.get(`${getApiBaseUrl()}/consent/current-version`)
            .then(res => setVersion(res.data))
            .catch(() => setError(t('consent:versionLoadFailed')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleAccept() {
        if (!checked) {
            setHighlight(true);
            setError(t('consent:scrollAndCheck'));
            return;
        }
        setSubmitting(true);
        setError('');
        setHighlight(false);
        try {
            await api.post('/consent/accept');
            onAccepted?.();
        } catch (err) {
            setError(err.response?.data?.message || t('consent:saveFailed'));
        }
        setSubmitting(false);
    }

    return (
        <div style={{
            border: '1px solid #c4b5fd',
            borderRadius: '14px',
            overflow: 'hidden',
            background: '#fff'
        }}>
            <div style={{
                padding: '14px 16px',
                background: 'linear-gradient(135deg, #f0ebff, #fff)',
                borderBottom: '1px solid var(--sp-gray-100)'
            }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#334155', lineHeight: 1.5 }}>
                    {context === 'updated' ? t('consent:introUpdated') : t('consent:intro')}
                </p>
                {version && (
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#64748b' }}>
                        {t('consent:currentVersion', { version: version.version })}
                        {version.changeSummary ? ` — ${version.changeSummary}` : ''}
                    </p>
                )}
            </div>

            <iframe
                title={t('consent:policyIframeTitle')}
                src={policySrc}
                style={{
                    width: '100%',
                    height: 'min(55vh, 480px)',
                    border: 'none',
                    display: 'block',
                    background: '#fff'
                }}
            />

            <div style={{
                padding: '16px',
                borderTop: '1px solid var(--sp-gray-100)',
                background: highlight ? '#fff5f5' : '#fafafa'
            }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1.45
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                            setChecked(e.target.checked);
                            setHighlight(false);
                            if (e.target.checked) setError('');
                        }}
                        style={{ marginTop: '3px', flexShrink: 0 }}
                    />
                    <span>{t('consent:checkboxLabel')}</span>
                </label>

                {highlight && !checked && (
                    <p style={{ color: '#c62828', fontSize: '12px', margin: '10px 0 0 28px' }}>
                        {t('consent:scrollAndCheck')}
                    </p>
                )}

                {error && (
                    <p style={{ color: '#c62828', fontSize: '13px', margin: '10px 0 0' }}>{error}</p>
                )}

                <button
                    type="button"
                    onClick={handleAccept}
                    disabled={submitting}
                    style={{
                        marginTop: '14px',
                        width: '100%',
                        padding: '14px',
                        borderRadius: '10px',
                        border: 'none',
                        background: submitting ? '#94a3b8' : '#EB3333',
                        color: '#fff',
                        fontWeight: '700',
                        fontSize: '15px',
                        cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                >
                    {submitting ? t('consent:submitting') : t('consent:acceptButton')}
                </button>
            </div>
        </div>
    );
}
